import React from 'react';
import dayjs from 'dayjs';
import { Button, Input, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { colors } from 'theme/tokens';

/**
 * Phần dùng chung của ba trang khu "Thành viên" / "Nhóm & quyền":
 * pages/access-groups, pages/access-permissions, pages/staff-confirm
 * (docs/to-chuc-lai-nhom-quyen/00-ban-do-va-phuong-an-2026-09-29.md).
 *
 * Đọc lỗi theo cùng cách với pages/star-shared/star-error.ts: interceptor axios ném
 * `error.response.data`, nên `err` = { statusCode, message: 'MÃ_LỖI', error: 'câu tiếng Việt' };
 * `message` có thể là mảng (class-validator).
 */

const CODE_RE = /^[A-Z][A-Z0-9_]{3,}$/;
const GENERIC =
  /^(Bad Request|Not Found|Conflict|Payload Too Large|Too Many Requests|Unprocessable Entity|Forbidden|Unauthorized|Internal Server Error|Service Unavailable)$/i;
const NEST_ROUTE_MISSING = /^Cannot (GET|POST|PUT|PATCH|DELETE) /;

function rawMessage(err: any): string {
  if (Array.isArray(err?.message)) return err.message.join(', ');
  if (typeof err?.message === 'string') return err.message;
  return '';
}

/** Mã lỗi nghiệp vụ (vd. APPROVAL_PERMISSION_LOCKED); không có thì chuỗi rỗng. */
export function apiErrorCode(err: any): string {
  if (typeof err?.code === 'string' && CODE_RE.test(err.code)) return err.code;
  const raw = rawMessage(err).trim();
  return CODE_RE.test(raw) ? raw : '';
}

export function apiErrorStatus(err: any): number | undefined {
  const status = Number(err?.statusCode || err?.status);
  return Number.isFinite(status) && status > 0 ? status : undefined;
}

/**
 * 404 vì máy chủ CHƯA CÓ đường dẫn này (backend đang chạy bản cũ), khác với 404 nghiệp vụ
 * có mã riêng (vd. STAFF_CLAIM_NOT_FOUND).
 */
export function isMissingEndpoint(err: any): boolean {
  return apiErrorStatus(err) === 404 && !apiErrorCode(err);
}

/** Câu báo lỗi tiếng Việt: ưu tiên bảng mã của trang, rồi tới câu máy chủ gửi kèm. */
export function apiErrorText(
  err: any,
  messages: Record<string, string>,
  fallback = 'Có lỗi xảy ra, vui lòng thử lại.'
): string {
  if (err?.networkError) return 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.';
  const code = apiErrorCode(err);
  if (code && messages[code]) return messages[code];
  const raw = rawMessage(err);
  const found = Object.keys(messages).find((key) => raw.includes(key));
  if (found) return messages[found];

  const description = typeof err?.error === 'string' ? err.error.trim() : '';
  if (description && !GENERIC.test(description) && !CODE_RE.test(description)) return description;

  const status = apiErrorStatus(err);
  if (status === 401 || status === 403) {
    return 'Tài khoản của bạn không được làm việc này, hoặc phiên đăng nhập đã hết.';
  }
  if (status === 404) return 'Không tìm thấy dữ liệu. Tải lại trang rồi thử lại.';
  if (status && status >= 500) return 'Máy chủ đang gặp lỗi, vui lòng thử lại sau.';
  if (raw && !CODE_RE.test(raw) && !NEST_ROUTE_MISSING.test(raw) && !GENERIC.test(raw)) return raw;
  return fallback;
}

/**
 * Câu tiếng Việt thật do máy chủ gửi (vd. `err.error`, `failed[i].text`); chữ chung của HTTP
 * ("Conflict"…) hay mã trơn ("STAFF_…") thì trả chuỗi rỗng.
 */
export function readableText(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : '';
  return text && !GENERIC.test(text) && !CODE_RE.test(text) ? text : '';
}

/** Thân phản hồi: `res.data`, chấp nhận cả dạng bọc `{ data: ... }`. */
export function unwrap<T>(res: any, looksRight: (body: any) => boolean): T | null {
  const body = res?.data;
  if (body && looksRight(body)) return body as T;
  if (body?.data && looksRight(body.data)) return body.data as T;
  return null;
}

/** Danh sách phân trang `{ items, total, page, limit }` (chấp nhận thêm `data`, `metadata.total`). */
export function unwrapPage<T>(res: any): { items: T[]; total: number } {
  const body = res?.data;
  if (Array.isArray(body)) return { items: body, total: body.length };
  const candidate = body?.items ?? body?.data ?? body?.rows ?? [];
  const items: T[] = Array.isArray(candidate) ? candidate : [];
  const total = body?.total ?? body?.metadata?.total ?? body?.meta?.total ?? items.length;
  return { items, total: Number(total) || 0 };
}

const integer = new Intl.NumberFormat('vi-VN');

export const fmtNumber = (value?: number | null) =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : integer.format(value);

export const fmtDateTime = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—';

export const fmtDate = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY') : '—';

/** Số căn thẳng hàng, không nhảy bề rộng khi đổi giá trị. */
export const TABULAR: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

/**
 * Email / TikTok ID dài hơn ô thì xuống dòng ở sau dấu "@", ".", "_" hoặc "-" (chèn <wbr>), không
 * gãy giữa chữ kiểu "hpmedia.lo / cal" như khi chỉ dùng overflow-wrap.
 */
export const Breakable = ({ text }: { text: string }) => {
  // split với nhóm bắt giữ lại dấu ngăn: "a.b@c" → ["a", ".", "b", "@", "c"]. Không dùng
  // lookbehind của regex: Safari cũ (WebView của app trên iOS < 16.4) không đọc được cú pháp đó.
  const chunks: string[] = [];
  text.split(/([@._-])/).forEach((piece, index) => {
    if (index % 2 === 1 && chunks.length) chunks[chunks.length - 1] += piece;
    else if (piece) chunks.push(piece);
  });
  return (
    <>
      {chunks.map((chunk, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>
          {index > 0 && <wbr />}
          {chunk}
        </span>
      ))}
    </>
  );
};

/** Lý do / ghi chú tối thiểu (cùng mức với MIN_REASON_LENGTH của máy chủ). */
export const MIN_REASON = 5;

export const reasonOk = (value: string, min = MIN_REASON) => value.trim().length >= min;

interface ReasonInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** 0 = không bắt buộc. */
  min?: number;
  placeholder?: string;
  disabled?: boolean;
}

/** Ô nhập lý do có đếm ký tự ngay khi gõ; chưa đủ thì dòng đếm nói rõ còn thiếu bao nhiêu. */
export const ReasonInput = ({
  id,
  label,
  value,
  onChange,
  min = MIN_REASON,
  placeholder,
  disabled,
}: ReasonInputProps) => {
  const typed = value.trim().length;
  const missing = Math.max(0, min - typed);
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
        {label}
      </label>
      <Input.TextArea
        id={id}
        value={value}
        disabled={disabled}
        maxLength={255}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder={placeholder}
        aria-describedby={`${id}-count`}
        onChange={(event) => onChange(event.target.value)}
      />
      <div
        id={`${id}-count`}
        aria-live="polite"
        style={{
          ...TABULAR,
          marginTop: 4,
          minHeight: 20,
          fontSize: 12,
          color: min > 0 && missing > 0 ? colors.warningInk : colors.textSecondary,
        }}
      >
        {min > 0
          ? missing > 0
            ? `Đã nhập ${typed} ký tự, cần ít nhất ${min} (còn thiếu ${missing}).`
            : `Đã nhập ${typed} ký tự, đủ rồi.`
          : `Đã nhập ${typed}/255 ký tự.`}
      </div>
    </div>
  );
};

/** Máy chủ chưa có đường dẫn (404): tính năng đang được đưa lên, không phải lỗi của người dùng. */
export const FeaturePending = ({ onRetry }: { onRetry?: () => void }) => (
  <Result
    status="info"
    title="Tính năng đang được bật"
    subTitle="Phần máy chủ của trang này đang được cài. Bạn chưa cần làm gì, quay lại sau ít phút rồi bấm Tải lại."
    extra={
      onRetry ? (
        <Button icon={<ReloadOutlined />} onClick={onRetry}>
          Tải lại
        </Button>
      ) : undefined
    }
  />
);

export const LoadFailed = ({
  title,
  text,
  onRetry,
}: {
  title: string;
  text: string;
  onRetry: () => void;
}) => (
  <Result
    status="error"
    title={title}
    subTitle={text}
    extra={
      <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
        Thử lại
      </Button>
    }
  />
);
